import _slicedToArray from "/opt/build/repo/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useRef, useReducer, useLayoutEffect, useEffect } from 'react';
function shallowEqual(objA, objB) {
  if (Object.is(objA, objB)) {
    return true;
  }
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  var keysA = Object.keys(objA);
  if (keysA.length !== Object.keys(objB).length) {
    return false;
  }
  for (var i = 0; i < keysA.length; i++) {
    if (!Object.prototype.hasOwnProperty.call(objB, keysA[i]) || !Object.is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}
var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
var reducer = function reducer(state, newState) {
  return newState;
};
function create(createState) {
  var listeners = new Set();
  var setState = function setState(partial) {
    var partialState = typeof partial === 'function' ? partial(state) : partial;
    if (partialState !== state) {
      state = Object.assign({}, state, partialState);
      listeners.forEach(function (listener) {
        return listener(state);
      });
    }
  };
  var getState = function getState() {
    return state;
  }; // Optional selector param goes first so we can infer its return type and use
  // it for listener

  var subscribe = function subscribe(selectorOrListener, listenerOrUndef) {
    var listener = selectorOrListener; // Existance of second param means a selector was passed in

    if (listenerOrUndef) {
      // We know selector is not type StateListener so it must be StateSelector
      var selector = selectorOrListener;
      var stateSlice = selector(state);
      listener = function listener() {
        var selectedSlice = selector(state);
        if (!shallowEqual(stateSlice, stateSlice = selectedSlice)) listenerOrUndef(stateSlice);
      };
    }
    listeners.add(listener);
    return function () {
      return void listeners.delete(listener);
    };
  };
  var destroy = function destroy() {
    listeners.clear();
  };
  var useStore = function useStore(selector, dependencies) {
    var selectorRef = useRef(selector);
    var depsRef = useRef(dependencies);
    var _useReducer = useReducer(reducer, state,
      // Optional third argument but required to not be 'undefined'
      selector),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      stateSlice = _useReducer2[0],
      dispatch = _useReducer2[1]; // Need to manually get state slice if selector has changed with no deps or
    // deps exist and have changed

    if (selector && (!dependencies && selector !== selectorRef.current || dependencies && !shallowEqual(dependencies, depsRef.current))) {
      stateSlice = selector(state);
    } // Update refs synchronously after view has been updated

    useIsomorphicLayoutEffect(function () {
      selectorRef.current = selector;
      depsRef.current = dependencies;
    }, dependencies || [selector]);
    useIsomorphicLayoutEffect(function () {
      return selector ? subscribe(
      // Truthy check because it might be possible to set selectorRef to
      // undefined and call this subscriber before it resubscribes
      function () {
        return selectorRef.current ? selectorRef.current(state) : state;
      }, dispatch) : subscribe(dispatch); // Only resubscribe to the store when changing selector from function to
      // undefined or undefined to function
    }, [!selector]);
    return stateSlice;
  };
  var api = {
    destroy: destroy,
    getState: getState,
    setState: setState,
    subscribe: subscribe
  };
  var state = createState(setState, getState, api);
  return [useStore, api];
}
export default create;